import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  fetchContentItemList,
  addContentItem,
  fetchContentItem,
  updateContentItem,
  patchContentItem,
  deleteContentItem,
} from '../../api/content/contentItems'
import handleToast from '../../components/HandleToast'
import { ContentItemDataPost } from '../../types/content/contentItems'

export function contentItems() {
  const toast = useToast()

  const useGetContentItemsListQuery = ({ id }: { id: string }) =>
    useQuery({
      queryKey: ['contentItemsList', id],
      queryFn: async () => {
        const data = await fetchContentItemList({ id })
        return data
      },
    })

  const createContentItemMutation = useMutation({
    mutationFn: async ({ id, contentItem }: { id: string; contentItem: ContentItemDataPost }) => {
      try {
        const result = await addContentItem(id, contentItem)
        return { id, result }
      } catch (error) {
        console.error('Error creating content item:', error)
        throw error
      } finally {
      }
    },
  })

  const getContentItemQuery = ({ id, itemId }: { id: string; itemId: string }) =>
    useQuery({
      queryKey: ['contentItem', id, itemId],
      queryFn: ({ queryKey }: { queryKey: [string, string, string] }) => {
        const [, id, itemId] = queryKey
        return fetchContentItem(id, itemId)
      },
    })

  const updateContentItemMutation = useMutation({
    mutationFn: async ({
      id,
      itemId,
      contentItem,
    }: {
      id: string
      itemId: string
      contentItem: ContentItemDataPost
    }) => {
      try {
        await updateContentItem(id, itemId, contentItem)
        return { id, itemId } // Return id and itemId for onSuccess handler
      } catch (error) {
        console.error('Error updating content item:', error)
        throw error
      } finally {
      }
    },
    onError: (error: Error) => {
      handleToast(toast, false, 'Update content item failed', error.message)
    },
  })

  const patchContentItemMutation = useMutation({
    mutationFn: async ({
      id,
      itemId,
      contentItem,
    }: {
      id: string
      itemId: string
      contentItem: ContentItemDataPost
    }) => {
      try {
        const result = await patchContentItem(id, itemId, contentItem)
        return { id, itemId, result } // Return id, itemId, and result for onSuccess handler
      } catch (error) {
        console.error('Error patching content item:', error)
        throw error
      } finally {
      }
    },
    // Let the component itself handle query invalidation to avoid multiple refetches
  })

  const deleteContentItemMutation = useMutation({
    mutationFn: async ({ id, itemId }: { id: string; itemId: string }) => {
      try {
        const result = await deleteContentItem(id, itemId)
        return { id, itemId, result } // Return id, itemId, and result for onSuccess handler
      } catch (error) {
        console.error('Error deleting content item:', error)
        throw error
      } finally {
      }
    },
    // Let the component itself handle query invalidation to avoid multiple refetches
  })

  return {
    useGetContentItemsListQuery,
    createContentItemMutation,
    getContentItemQuery,
    updateContentItemMutation,
    patchContentItemMutation,
    deleteContentItemMutation,
  }
}
